"use client";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import {
  FilterCommission,
  CommissionForm,
  CommissionSettlementForm,
  UseCommissionProps,
} from "@/types";
import { toast } from "react-toastify";
import {
  fetchFullCommission,
  fetchsubscriptionCommission,
  normalCommissionSettlement,
  subscriptionCommissionSettlement,
} from "@/store/modules/fullCommission/thunks";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubscriptionCommissionSchema } from "@/validations/subscriptionCommission";

const useSubscriptionCommission = ({
  filter,
  onSuccess,
}: UseCommissionProps) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { currency } = useAuth();

  const [
    searchDataSubscriptionCommission,
    setSearchDataSubscriptionCommission,
  ] = useState<FilterCommission | null>(null);

  const [isSettlement, setIsSettlement] = useState(false);

  const [hasSearched, setHasSearched] = useState(false);

  const resolver = useMemo(() => {
    return yupResolver(SubscriptionCommissionSchema, {
      context: { isSettlement },
    }) as Resolver<CommissionForm>;
  }, [isSettlement]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<CommissionForm>({
    resolver,
    context: { isSettlement },
    defaultValues: {
      employee_id: "",
      startDate: undefined,
      endDate: undefined,
      payment_method_id: null,
      paid_at: null,
    },
  });

  const { subscriptionCommission, loading, error } = useSelector(
    (state: any) => state.fullCommission,
  );
  const dispatch: AppDispatch = useDispatch();

  const handleSubmitFilter = async (data: FilterCommission) => {
    setIsSettlement(false);
    const { startDate, endDate, employee_id } = data;

    if (!startDate || !endDate) {
      toast.error("Preencha o intervalo de datas!");
      return;
    }

    const payload: Record<string, any> = {
      ...params,
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
      employee_id: employee_id,
    };

    try {
      await dispatch(fetchsubscriptionCommission(payload)).unwrap();
      setSearchDataSubscriptionCommission(data);
    } catch (err: any) {
      if (err?.errors) {
        Object.values(err.errors)
          .flat()
          .forEach((msg: any) => {
            toast.error(msg);
          });
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Erro ao buscar dados financeiros.");
      }
    }
  };

  const handleSubmitSubscriptionCommissionSettlement = async (
    data: CommissionSettlementForm,
  ) => {
    setIsSettlement(true);

    const payload: Record<string, any> = {
      start_date: data.startDate.toISOString().split("T")[0],
      end_date: data.endDate.toISOString().split("T")[0],
      employee_id: data.employee_id,
      paid_at: data.paid_at ? data.paid_at.toISOString().split("T")[0] : null,
      payment_method_id: data.payment_method_id,
    };

    try {
      await dispatch(subscriptionCommissionSettlement(payload)).unwrap();
      if (onSuccess) onSuccess();

      const startDate = data.startDate.toISOString().split("T")[0];
      const endDate = data.endDate.toISOString().split("T")[0];
      reset();
      dispatch(
        fetchFullCommission({
          ...params,
          start_date: startDate,
          end_date: endDate,
        } as any),
      );
    } catch (err: any) {
      if (err?.errors) {
        Object.values(err.errors)
          .flat()
          .forEach((msg: any) => toast.error(msg));
      } else if (err?.message) {
        toast.error(err.message);
      }
    }
  };

  const onSubmitSubscriptionCommission = (
    data: any,
    overrideIsSettlement?: boolean,
  ) => {
    const doSettlement =
      typeof overrideIsSettlement === "boolean"
        ? overrideIsSettlement
        : isSettlement;

    if (doSettlement) {
      setIsSettlement(true);
      handleSubmitSubscriptionCommissionSettlement(data);
    } else {
      setIsSettlement(false);
      handleSubmitFilter(data);
      setHasSearched(true);
    }
  };

  const totalItemsUsageSubscription =
    subscriptionCommission?.data?.usages?.total ?? 0;
  const itemsPerPagerUsageSubscription =
    subscriptionCommission?.data?.usages?.per_page ?? 10;
  const totalPagesUsageSubscription =
    totalItemsUsageSubscription > 0
      ? Math.ceil(totalItemsUsageSubscription / itemsPerPagerUsageSubscription)
      : 1;

  const [
    currentPagerPagesUsageSubscription,
    setCurrentPagerPagesUsageSubscription,
  ] = useState(subscriptionCommission?.data?.usages?.current_page ?? 1);

  useEffect(() => {
    const page = subscriptionCommission?.data?.usages?.current_page;
    if (page && page !== currentPagerPagesUsageSubscription) {
      setCurrentPagerPagesUsageSubscription(page);
    }
  }, [subscriptionCommission?.data?.usages?.current_page]);

  const handlePageChangePagesUsageSubscription = (page: number) => {
    setCurrentPagerPagesUsageSubscription(page);
    handlePage(page);
  };

  return {
    subscriptionCommission,
    loading,
    error,
    handleSubmitFilter,
    currency,
    register,
    handleSubmit,
    reset,
    control,
    errors,
    isSubmitting,
    searchDataSubscriptionCommission,
    handleSubmitSubscriptionCommissionSettlement,
    onSubmitSubscriptionCommission,
    hasSearched,
    //setSubmitType,
    setIsSettlement,
    totalItemsUsageSubscription,
    itemsPerPagerUsageSubscription,
    totalPagesUsageSubscription,
    currentPagerPagesUsageSubscription,
    handlePageChangePagesUsageSubscription,
    // expose some helpers for parent components to set form errors / read values
    getValues,
    setValue,
    setError,
    clearErrors,
  };
};

export default useSubscriptionCommission;
